import React, { createContext, useState, useContext, useCallback, useRef, useEffect } from 'react';
import BranchSetupStep from './journey-wizard/steps/BranchSetupStep';
import { ModernButton, ModernCard } from './ui/modern';
import { toast } from '../utils/toast';
import api from '../api';
import { getDefaultBranch } from './journey-wizard/templates';

// Create a local journey context for standalone use
export const StandaloneJourneyContext = createContext();

export const useStandaloneJourney = () => {
  const context = useContext(StandaloneJourneyContext);
  if (!context) {
    throw new Error('useStandaloneJourney must be used within StandaloneJourneyProvider');
  }
  return context;
};

/**
 * Standalone wrapper for BranchSetupStep that provides journey context locally
 * and handles API submission without the full journey wizard.
 */
function StandaloneBranchSetup({ company, companyId, onSuccess }) {
  const [branches, setBranches] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const validateRef = useRef(null);

  // Automatically add first branch when component mounts
  useEffect(() => {
    if (branches.length === 0) {
      const firstBranch = getDefaultBranch(null);
      setBranches([firstBranch]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Branch management functions
  const addBranch = useCallback((branch) => {
    setBranches(prev => [...prev, branch]);
  }, []);

  const updateBranch = useCallback((index, branch) => {
    setBranches(prev => {
      const newBranches = [...prev];
      newBranches[index] = branch;
      return newBranches;
    });
  }, []);

  const removeBranch = useCallback((index) => {
    setBranches(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updateBranches = useCallback((newBranches) => {
    setBranches(newBranches);
  }, []);

  // Handle validation callback from BranchSetupStep
  const handleValidate = useCallback((validateFn) => {
    validateRef.current = validateFn;
  }, []);

  // Submit all branches to the API
  const handleSaveBranches = async () => {
    // Validate all branches first
    if (validateRef.current) {
      const validationResult = validateRef.current();
      if (!validationResult.valid) {
        toast.error(validationResult.message || 'Please complete all branch information');
        return;
      }
    }

    if (branches.length === 0) {
      toast.error('Please add at least one branch');
      return;
    }

    setIsSubmitting(true);
    const results = {
      success: [],
      failed: [],
    };

    try {
      // Submit branches sequentially
      for (let i = 0; i < branches.length; i++) {
        const branch = branches[i];
        try {
          const payload = {
            name: branch.name,
            contactPerson: branch.contactPerson,
            contactNumber: branch.contactNumber,
            emailAddress: branch.emailAddress,
            address: branch.address || '',
            city: branch.city,
            province: branch.province || '',
            postalCode: branch.postalCode || '',
            country: branch.country || '',
            hasFirewall: branch.hasFirewall || false,
            numberOfUsers: Number(branch.numberOfUsers) || 0,
            numberOfDevices: Number(branch.numberOfDevices) || 0,
            numberOfNetworkDevices: Number(branch.numberOfNetworkDevices) || 0,
            numberOfServers: Number(branch.numberOfServers) || 0,
          };

          const response = await api.post(`/companies/${companyId}/branches`, payload);
          results.success.push({ name: branch.name, data: response.data });
        } catch (error) {
          console.error(`Error adding branch ${branch.name}:`, error);
          const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
          results.failed.push({ name: branch.name, error: errorMsg });
        }
      }

      // Show results
      if (results.success.length > 0) {
        toast.success(`Successfully added ${results.success.length} branch(es)!`);
      }

      if (results.failed.length > 0) {
        const failedNames = results.failed.map(f => f.name).join(', ');
        toast.error(`Failed to add: ${failedNames}`);
      }

      // If all succeeded, call onSuccess callback
      if (results.failed.length === 0) {
        setBranches([]); // Reset form
        if (onSuccess) {
          onSuccess();
        }
      } else {
        // Remove successful branches from the form, keep failed ones
        const failedBranchNames = results.failed.map(f => f.name);
        setBranches(prev => prev.filter(b => failedBranchNames.includes(b.name)));
      }
    } catch (error) {
      console.error('Unexpected error during branch submission:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Context value matching journey wizard's API
  const contextValue = {
    branches,
    company: company || {},
    addBranch,
    updateBranch,
    removeBranch,
    updateBranches,
    selectedTemplate: null, // Not used in standalone mode
  };

  return (
    <StandaloneJourneyContext.Provider value={contextValue}>
      <ModernCard className="mb-6 bg-gradient-to-br from-slate-900/85 via-slate-900/70 to-slate-800/70 border border-white/5 shadow-2xl">
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-white mb-2">Add Branches</h3>
          <p className="text-slate-400 text-sm">
            Use the same advanced interface from the onboarding journey. Add multiple branches, bulk import, and more.
          </p>
        </div>

        {/* Render the BranchSetupStep component */}
        <BranchSetupStep onValidate={handleValidate} />

        {/* Action buttons */}
        {branches.length > 0 && (
          <div className="mt-6 pt-6 border-t border-white/5 flex justify-end gap-3">
            <ModernButton
              variant="secondary"
              onClick={() => {
                if (window.confirm('Discard all branches and close?')) {
                  setBranches([]);
                  if (onSuccess) onSuccess();
                }
              }}
              disabled={isSubmitting}
            >
              Cancel
            </ModernButton>
            <ModernButton
              onClick={handleSaveBranches}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : `Save ${branches.length} Branch${branches.length > 1 ? 'es' : ''}`}
            </ModernButton>
          </div>
        )}
      </ModernCard>
    </StandaloneJourneyContext.Provider>
  );
}

export default StandaloneBranchSetup;

